// api.js
import axios from 'axios';
import config from './config';

// Base URL for the backend API
const API_BASE_URL = config.API_BASE_URL;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to include the auth token in requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Authentication APIs
export const register = async (name, email, password) => {
  const response = await api.post('/auth/register', { name, email, password });
  return response.data;
};

export const login = async (email, password) => {
  const response = await api.post('/auth/login', { email, password });
  return response.data;
};

export const forgotPassword = async (email) => {
  const response = await api.post('/auth/forgot-password', { email });
  return response.data;
};

export const verifyResetToken = async (token) => {
  const response = await api.get(`/auth/reset-password/${token}/verify`);
  return response.data;
};

export const resetPassword = async (token, password) => {
  const response = await api.post(`/auth/reset-password/${token}`, { password });
  return response.data;
};

export const getProfile = async () => {
  const response = await api.get('/auth/profile');
  return response.data;
};

export const updateProfile = async (userData) => {
  const response = await api.put('/auth/profile', userData);
  return response.data;
};

export const changePassword = async (currentPassword, newPassword) => {
  const response = await api.post('/auth/change-password', { currentPassword, newPassword });
  return response.data;
};

// Fetch all subjects for a given curriculum
export const getSubjects = async (curriculum) => {
  try {
    const response = await api.get('/subjects', {
      params: { curriculum },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching subjects:', error);
    return [];
  }
};

// Fetch all years for a given curriculum and subject
export const getYears = async (curriculum, subject) => {
  try {
    const response = await api.get('/years', {
      params: { curriculum, subject },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching years:', error);
    return [];
  }
};

// Fetch all sessions for a given curriculum, subject, and year
export const getSessions = async (curriculum, subject, year) => {
  try {
    const response = await api.get('/sessions', {
      params: { curriculum, subject, year },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching sessions:', error);
    return [];
  }
};

// Fetch all papers for a given curriculum, subject, year, and session
export const getPapers = async (curriculum, subject, year, session) => {
  try {
    const response = await api.get('/papers', {
      params: { curriculum, subject, year, session },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching papers:', error);
    return [];
  }
};

// Get the URL for a specific PDF file
export const getPdfUrl = (filePath) => {
  return `${API_BASE_URL}/pdf/${filePath}`;
};

// Send a chat message to the AI and stream the response
export const sendChatMessage = async (messages, onDataReceived) => {
  try {
    const response = await fetch(`${API_BASE_URL}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify({ messages }),
    });

    if (!response.ok) {
      throw new Error('Failed to send chat message');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value);
      const lines = chunk.split('\n');

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            console.log('sendChatMessage token:', data);
            onDataReceived(data);
          } catch (e) {
            console.error('Error parsing token:', e);
          }
        }
      }
    }
  } catch (error) {
    console.error('Error sending chat message:', error);
  }
};